import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import type { FullDataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { exportDataView } from '@adornis/data-view/db/export-data-view.js';
import { alertDialog, confirmDialog, guardDialog, waitForDialog } from '@adornis/dialog/dialog-functions.js';
import { downloadLink } from '@adornis/file-utils/client/helper-methods.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { useLocalStorage } from '@adornis/functional-lit/use-local-storage.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { ListOperationsMenu } from '@martinus/styles/client/menus/ListOperationsMenu.js';
import { format } from 'date-fns';
import { showLodasExportConfigurationDialog } from '../../../client/components/dialogs/sg-lodas-export-configuration.js';
import { sessionExtend } from '../../../client/session-timeout.js';
import { AbwesenheitAPI } from '../../../abwesenheiten/api/AbwesenheitAPI.js';
import { openAustrittDialog } from '../../../austritt/client/AustrittDialog.js';
import { SgSnackbar } from '../../../client/components/ui/sg-snackbar.js';
import { exportLodasDaten } from '../../../db/import-export/lodas-export.js';
import { PermissionAPI } from '../../../permissions/api/PermissionAPI.js';
import { useMartinusABAC } from '../../../permissions/client/useMartinusAbac.js';
import { SepaAPI } from '../../../sepa/api/SepaAPI.js';
import { openSepaExportSettingsDialog } from '../../../sepa/client/SepaExportSettingsDialog.js';
import { sendFreeTextEmailDialog } from '../../../user-emails/free-text-mails/sg-dialog-send-free-mail.js';
import { openCreateUserDialog } from '../../client/CreateUserDialog.js';
import { UserDetailsActions } from '../../details/UserDetailsPolicy.js';
import { TicketAPI } from '../../../ticket/api/TicketAPI.js';
import type { Person } from '../../entities/person.js';
import { UserExport } from '../../export/UserExport.js';
import { openPersonImportSyntaxInformationDialog } from '../../import/client/PersonImportSyntaxInformationDialog.js';
import {
  recentPersonImportLabel,
  removePersonImport,
  useRecentPersonImports,
} from '../../import/client/recentPersonImports.js';
import { reviewPersonImport, startPersonImportFlow } from '../../import/client/startPersonImportFlow.js';
import { UserActions } from '../../UserPolicy.js';
import { UserListAPI } from '../api/UserListAPI.js';
import type { UserListSelectionInterface } from '../entities/UserListSelectionInterface.js';
import { UserListActions } from '../UserListPolicy.js';
import { fullDataviewWhitelist, simpleDataviewWhitelist } from './UserListSelectionsets.js';
import {
  getUnwindablePathsFromSelectionSet,
  openUserListUnwindPathSelectionDialog,
} from './UserListUnwindPathSelectionDialog.js';

/**
 * TODO maybe move this somewhere else? it is only used here for admins
 * Removes the selected users from the database.
 * @param selectedInterface The selected users to remove.
 * @returns A promise that resolves when the users have been removed.
 */
async function removeSelectedUsers(selectedInterface: UserListSelectionInterface | null) {
  if (!selectedInterface) return;

  const numberOfSelection = await UserListAPI.previewSelection(selectedInterface)();

  if (!(await confirmDialog(`Sind Sie sicher, dass Sie ${numberOfSelection} Personen löschen möchten?`))) return;

  const interval = setInterval(() => {
    sessionExtend();
  }, 1000);

  await guardDialog(
    () => {
      if (!selectedInterface) return Promise.reject(new Error('No interface selected'));
      return UserListAPI.deleteBySelection(selectedInterface)();
    },
    {
      success: async e => {
        await alertDialog(`Es wurden ${e} Personen gelöscht.`);
      },
    },
  );

  clearInterval(interval);
}

export const UserListOperations = stateful(
  (props: {
    dataviewConfig: FullDataViewOptions<typeof Person>;
    selectedInterface: UserListSelectionInterface | null;
    setShowFullPath: (show: boolean) => void;
  }) => {
    const [showFullPath, setShowFullPath] = useLocalStorage('showFullPathUserList', false);

    useEffect(() => {
      props.setShowFullPath(showFullPath);
    }, [showFullPath]);

    const { can: canCreateUser } = useMartinusABAC({
      action: UserActions.CREATE_USER,
    });

    const { can: canRemoveUser } = useMartinusABAC({
      action: UserActions.REMOVE_USER,
    });

    const { can: canEditBookings } = useMartinusABAC({
      action: UserListActions.USER_LIST_BOOKINGS_EDIT,
    });

    const { can: canSendEmail } = useMartinusABAC({
      action: UserListActions.USER_LIST_EMAIL_SEND,
    });

    const { can: canRecalculateRoles } = useMartinusABAC({
      action: UserDetailsActions.VIEW_PERMISSION_MATRIX,
    });

    const { can: canToggleFullPersonsView } = useMartinusABAC({
      action: UserListActions.USER_LIST_FULL_FEATURES,
    });

    const { can: canExportLodas } = useMartinusABAC({
      action: UserListActions.USER_LIST_EXPORT_LODAS,
    });

    const { can: canExportTicket } = useMartinusABAC({
      action: UserListActions.USER_LIST_EXPORT_TICKET,
    });

    const { can: canExportSepa } = useMartinusABAC({
      action: UserListActions.USER_LIST_EXPORT_SEPA,
    });

    const { can: canExportAbwesenheit } = useMartinusABAC({
      action: UserListActions.USER_LIST_EXPORT_ABWESENHEIT,
    });

    const { can: canExportEdooSys } = useMartinusABAC({
      action: UserListActions.USER_LIST_EXPORT_EDOO_SYS,
    });

    const { can: canSendWaitlistUpdateLinks } = useMartinusABAC({
      action: UserListActions.USER_LIST_SEND_SUS_WAITLIST_UPDATE_LINKS,
    });

    const { can: canImportPerson } = useMartinusABAC({
      action: UserListActions.USER_LIST_IMPORT_PERSON,
    });
    const recentImports = useRecentPersonImports(!!canImportPerson);

    const singlePersonID =
      !props.selectedInterface?.filter && props.selectedInterface?.userIDs?.length === 1
        ? props.selectedInterface.userIDs[0]
        : null;
    /**
     * The import whose delete button was clicked once. A second click removes it - asked inside the menu,
     * because a confirm dialog would take the focus and close it. Disarms itself after a few seconds.
     */
    const [armedRemoval, setArmedRemoval] = useState<string | null>(null);
    useEffect(() => {
      if (!armedRemoval) return;
      const timeout = setTimeout(() => setArmedRemoval(null), 4000);
      return () => clearTimeout(timeout);
    }, [armedRemoval]);

    return ListOperationsMenu({
      sections: [
        {
          title: 'Verwaltung',
          subtext: !props.selectedInterface
            ? 'Einige Optionen benötigen eine Auswahl von Einträgen. Stellen Sie sicher, dass mindestens ein Eintrag ausgewählt ist um die jeweilige Option benutzen zu können.'
            : '',
          items: [
            ...(canToggleFullPersonsView
              ? [
                  {
                    label: showFullPath ? 'Vollständige Ansicht' : 'Kurzansicht',
                    iconifyClass: showFullPath
                      ? 'icon-[material-symbols--toggle-off-outline]'
                      : 'icon-[material-symbols--toggle-on-outline]',
                    additionalClasses: 'hover:bg-primary/50',
                    onClick: () => setShowFullPath(!showFullPath),
                  },
                ]
              : []),
            ...(canCreateUser
              ? [
                  {
                    label: 'Person anlegen',
                    iconifyClass: 'icon-[material-symbols--person-add-rounded]',
                    additionalClasses: 'hover:bg-success/50',
                    onClick: openCreateUserDialog,
                  },
                ]
              : []),

            ...(canEditBookings
              ? [
                  [
                    {
                      label: 'Buchungen bearbeiten',
                      iconifyClass: 'icon-[material-symbols--group-add-rounded]',
                      additionalClasses: 'hover:bg-success/50',
                      active: !props.selectedInterface,
                      joinedWidthPercent: 50,
                      onClick: () => {
                        if (!props.selectedInterface) return;

                        const selectionString = JSON.stringify(props.selectedInterface);
                        goTo(`/bookings?selection=${selectionString}`);
                      },
                    },
                    {
                      label: 'Buchung hinzufügen',
                      iconifyClass: 'icon-[material-symbols--add-rounded]',
                      additionalClasses: 'hover:bg-success/50',
                      active: !props.selectedInterface,
                      joinedWidthPercent: 50,
                      onClick: () => {
                        if (!props.selectedInterface) return;

                        const selectionString = JSON.stringify(props.selectedInterface);
                        goTo(`/bookings?selection=${selectionString}&createBooking=true`);
                      },
                    },
                  ],
                  {
                    label: 'Austritt',
                    iconifyClass: 'icon-[material-symbols--person-remove-rounded]',
                    additionalClasses: 'hover:bg-error/50',
                    // one person picked by hand: "Alle auswählen" counts as a filter even when it matches one
                    active: !singlePersonID,
                    onClick: async () => {
                      if (!singlePersonID) return;
                      const ended = await openAustrittDialog(singlePersonID);
                      if (ended !== null)
                        void SgSnackbar.show(`Austritt durchgeführt: ${ended} Mitgliedschaften beendet oder gelöscht`);
                    },
                  },
                ]
              : []),
            ...(canSendEmail
              ? [
                  {
                    label: 'E-Mail',
                    iconifyClass: 'icon-[material-symbols--send-rounded]',
                    additionalClasses: 'hover:bg-primary/50',
                    active: !props.selectedInterface,
                    onClick: async () => {
                      if (!props.selectedInterface) return;
                      await sendFreeTextEmailDialog(props.selectedInterface);
                    },
                  },
                ]
              : []),
            ...(canSendWaitlistUpdateLinks
              ? [
                  {
                    label: 'Wartelisten-Aktualisierung',
                    iconifyClass: 'icon-[material-symbols--forward-to-inbox-outline-rounded]',
                    additionalClasses: 'hover:bg-primary/50',
                    active: !props.selectedInterface,
                    onClick: async () => {
                      if (!props.selectedInterface) return;
                      const numberOfSelection = await UserListAPI.previewSelection(props.selectedInterface)();
                      if (
                        !(await confirmDialog(
                          `Den Sorgeberechtigten der ${numberOfSelection} ausgewählten Personen den Link zur Aktualisierung ihrer Schulplatzanfrage senden? Personen ohne laufende Bewerbung werden übersprungen.`,
                        ))
                      )
                        return;
                      await guardDialog(
                        () => {
                          if (!props.selectedInterface) return Promise.reject(new Error('No interface selected'));
                          return UserListAPI.sendSuSWaitlistUpdateLinks(props.selectedInterface)();
                        },
                        {
                          success: async sent =>
                            alertDialog(`Es wurden ${sent} E-Mails an Sorgeberechtigte versendet.`),
                        },
                      );
                    },
                  },
                ]
              : []),
            ...(canRecalculateRoles
              ? [
                  {
                    label: 'Rollen aktualisieren',
                    iconifyClass: 'icon-[material-symbols--refresh-rounded]',
                    additionalClasses: 'hover:bg-info/50',
                    active: !props.selectedInterface,
                    onClick: async () => {
                      if (!props.selectedInterface) return;

                      const numberOfSelection = await UserListAPI.previewSelection(props.selectedInterface)();

                      if (
                        !(await confirmDialog(
                          `Sind Sie sicher, dass Sie ${numberOfSelection} Personen aktualisieren möchten?`,
                        ))
                      )
                        return;

                      // only queued here - the permission sync worker recalculates them, its backlog shows on the admin dashboard
                      await guardDialog(
                        () => {
                          if (!props.selectedInterface) return Promise.reject(new Error('No interface selected'));
                          return PermissionAPI.recalculateForListSelection(props.selectedInterface)();
                        },
                        {
                          success: () => {
                            void XSnackbar.show(
                              `Die Rollen von ${numberOfSelection} Personen werden im Hintergrund aktualisiert.`,
                            );
                          },
                        },
                      );
                    },
                  },
                ]
              : []),

            ...(canRemoveUser
              ? [
                  {
                    label: 'Personen löschen',
                    iconifyClass: 'icon-[material-symbols--person-remove-rounded]',
                    additionalClasses: 'hover:bg-error/50',
                    active: !props.selectedInterface,
                    onClick: () => removeSelectedUsers(props.selectedInterface),
                  },
                ]
              : []),
          ],
        },
        {
          title: 'Exports',
          items: [
            {
              label: 'Aktuelle Ansicht exportieren',
              iconifyClass: 'icon-[material-symbols--download-rounded]',
              additionalClasses: 'hover:bg-neutral/50',
              active: !props.selectedInterface,
              onClick: async () => {
                if (!props.selectedInterface) return;

                const filter = props.dataviewConfig.view.filter;
                if (!filter) return;

                if (props.selectedInterface.userIDs?.length)
                  filter.setFilterAspect(
                    new StringAspect({
                      fieldName: '_id',
                      mode: 'containsOR',
                      value: props.selectedInterface.userIDs.join(','),
                    }),
                  );

                if (props.selectedInterface.explicitDeselectedUserIDs?.length)
                  filter.setFilterAspect(
                    new StringAspect({
                      fieldName: '_id',
                      mode: 'containsNONE',
                      value: props.selectedInterface.explicitDeselectedUserIDs.join(','),
                    }),
                  );

                try {
                  const activeSelectionSet = canToggleFullPersonsView ? fullDataviewWhitelist : simpleDataviewWhitelist;
                  const unwindablePaths = getUnwindablePathsFromSelectionSet(
                    props.dataviewConfig.class,
                    activeSelectionSet,
                  );

                  const choice = await openUserListUnwindPathSelectionDialog(unwindablePaths);
                  if (!choice) return;

                  await exportDataView(props.dataviewConfig, {
                    fileName: `Export_Personen_${format(new Date(), 'yyyy-MM-dd-HH-mm')}`,
                    exportClass: UserExport,
                    unwindPath: choice.unwindPath ?? undefined,
                    format: choice.fileFormat,
                  });
                } finally {
                  filter.removeFilterAspect('_id');
                }
              },
            },
            ...(canExportEdooSys
              ? [
                  {
                    label: 'edoo.sys-Export',
                    iconifyClass: 'icon-[material-symbols--download-rounded]',
                    additionalClasses: 'hover:bg-neutral/50',
                    active: !props.selectedInterface,
                    onClick: async () => {
                      if (!props.selectedInterface) return;

                      const url = await waitForDialog(
                        UserListAPI.exportEdooSys(props.selectedInterface)(),
                        'edoo.sys-Export wird generiert...',
                      );
                      await downloadLink(url, `edoo.sys_Bewerber_${format(new Date(), 'yyyy-MM-dd-HH-mm')}.csv`);
                    },
                  },
                ]
              : []),
            ...(canExportLodas
              ? [
                  {
                    label: 'LODAS-Export',
                    iconifyClass: 'icon-[material-symbols--download-rounded]',
                    additionalClasses: 'hover:bg-neutral/50',
                    active: !props.selectedInterface,
                    onClick: async () => {
                      if (!props.selectedInterface) return;

                      const config = await showLodasExportConfigurationDialog();
                      if (!config) return;

                      const result = await guardDialog(
                        () => {
                          if (!props.selectedInterface) return Promise.reject(new Error('No interface selected'));
                          return exportLodasDaten(props.selectedInterface, config)();
                        },
                        {
                          success: () => {
                            void XSnackbar.show('Lodas-Export wurde erfolgreich generiert.');
                          },
                        },
                      );

                      if (!result) return;

                      await downloadLink(
                        result,
                        `Lodas_${config.type === 'stammdaten' ? 'Stammdaten' : 'Bewegungsdaten'}_${format(
                          new Date(),
                          'dd.MM.yyyy hh:mm',
                        )}.txt`,
                      );
                    },
                  },
                ]
              : []),
            ...(canExportTicket
              ? [
                  {
                    label: 'Ticket-Export',
                    iconifyClass: 'icon-[material-symbols--download-rounded]',
                    additionalClasses: 'hover:bg-neutral/50',
                    onClick: async () => {
                      const ticketCsvUrl = await waitForDialog(
                        TicketAPI.downloadCSV()(),
                        'Ticket-CSV wird generiert...',
                      );
                      await downloadLink(ticketCsvUrl, `${format(new Date(), 'yyyy-MM')}_GK_Schulgesellschaft`);
                    },
                  },
                ]
              : []),
            ...(canExportSepa
              ? [
                  {
                    label: 'SEPA-Export',
                    iconifyClass: 'icon-[material-symbols--download-rounded]',
                    additionalClasses: 'hover:bg-neutral/50',
                    active: !props.selectedInterface,
                    onClick: async () => {
                      if (!props.selectedInterface) return;

                      const settings = await openSepaExportSettingsDialog(props.selectedInterface);
                      if (!settings) return;

                      await guardDialog(() => SepaAPI.export(settings)(), {
                        success: async result => {
                          const url = AdornisFile.getDownloadLink(result as string);
                          await downloadLink(url, 'sepa.zip');
                        },
                      });
                    },
                  },
                ]
              : []),
            ...(canExportAbwesenheit
              ? [
                  {
                    label: 'Abwesenheits-Export',
                    iconifyClass: 'icon-[material-symbols--download-rounded]',
                    additionalClasses: 'hover:bg-neutral/50',
                    active: !props.selectedInterface,
                    onClick: async () => {
                      if (!props.selectedInterface) return;

                      const result = await waitForDialog(
                        AbwesenheitAPI.downloadCSV()(),
                        'Abwesenheits-CSV wird generiert...',
                      );
                      await downloadLink(result, 'abwesenheiten.csv');
                    },
                  },
                ]
              : []),
          ],
        },
        {
          title: 'Imports',
          items: [
            ...(canImportPerson
              ? [
                  [
                    {
                      label: 'Person-Import',
                      iconifyClass: 'icon-[material-symbols--database-upload-rounded]',
                      additionalClasses: 'hover:bg-success/50',
                      joinedWidthPercent: 80,
                      onClick: async () => {
                        await startPersonImportFlow();
                      },
                    },
                    {
                      iconifyClass: 'icon-[material-symbols--code-xml]',
                      additionalClasses: 'hover:bg-neutral/50',
                      joinedWidthPercent: 20,
                      onClick: () => {
                        openPersonImportSyntaxInformationDialog();
                      },
                    },
                  ],
                  // an import left open: reopen it, or throw it away
                  ...recentImports.map(entry => {
                    const armed = armedRemoval === entry.importProcessId;
                    return [
                      {
                        label: recentPersonImportLabel(entry),
                        iconifyClass: 'icon-[material-symbols--history-rounded]',
                        additionalClasses: 'hover:bg-neutral/50',
                        joinedWidthPercent: armed ? 55 : 80,
                        onClick: () => reviewPersonImport(entry.importProcessId ?? ''),
                      },
                      armed
                        ? {
                            label: 'Entfernen?',
                            iconifyClass: 'icon-[material-symbols--delete-forever-rounded]',
                            additionalClasses: 'bg-error text-error-content',
                            joinedWidthPercent: 45,
                            onClick: async () => {
                              setArmedRemoval(null);
                              await removePersonImport(entry);
                            },
                          }
                        : {
                            iconifyClass: 'icon-[material-symbols--delete-outline-rounded]',
                            additionalClasses: 'hover:bg-error/50',
                            joinedWidthPercent: 20,
                            onClick: () => setArmedRemoval(entry.importProcessId ?? null),
                          },
                    ];
                  }),
                ]
              : []),
          ],
        },
      ],
    });
  },
);
